import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/features/auth/hooks/useAuthContext";
import { useProjects } from "@/hooks/useProjects";
import { queryKeys } from "@/lib/query-keys";
import { useIsOnboardingFlowCompleted } from "@/services/query/feature-params-queries";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProjectListComponent } from "./components/project-list";

export default function WorkspacePage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState("");
    const { user } = useAuth();

    // Get user details
    const firstName = user?.user_metadata?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || "Author";
    const avatarUrl = user?.user_metadata?.avatar_url;
    const userInitials = firstName.substring(0, 2).toUpperCase();

    // Check if onboarding flow is completed
    const {
        data: isOnboardingCompleted = false,
        isLoading: isOnboardingCheckLoading
    } = useIsOnboardingFlowCompleted();

    // Force refresh projects when entering workspace to avoid empty states
    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() });
    }, [queryClient]);

    // Redirect to onboarding flow if not completed
    useEffect(() => {
        if (!isOnboardingCheckLoading && !isOnboardingCompleted) {
            console.log('Onboarding flow not completed, redirecting...');
            navigate('/onboarding-flow');
        }
    }, [isOnboardingCompleted, isOnboardingCheckLoading, navigate]);

    // If still checking onboarding status, show loading state
    if (isOnboardingCheckLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                    <p className="text-muted-foreground">Loading your workspace...</p>
                </div>
            </div>
        );
    }

    // If not completed and not loading, we're in the process of redirecting
    // Don't render the workspace content to avoid flashing
    if (!isOnboardingCompleted) {
        return null;
    }

    // Function to refresh projects
    const handleRefreshProjects = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
        queryClient.refetchQueries({ queryKey: queryKeys.projects.lists() });
    };

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col">
            {/* Top Navigation Bar */}
            <motion.header
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
            >
                <div className="container max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-lg tracking-tight">Plot Studio</span>
                    </div>

                    <div className="flex items-center gap-6 text-sm font-medium text-muted-foreground">
                        <button onClick={() => navigate('/onboarding-flow')} className="hover:text-foreground transition-colors">Guides</button>
                        {/* <button className="hover:text-foreground transition-colors relative">
                            Updates
                            <span className="absolute -top-1 -right-1.5 h-2 w-2 rounded-full bg-red-500"></span>
                        </button> */}
                        <div className="h-4 w-px bg-border mx-1"></div>
                        <div className="flex items-center gap-3 pl-2">
                            <Avatar className="h-8 w-8 border border-border cursor-pointer" onClick={() => navigate('/account')}>
                                <AvatarImage src={avatarUrl} />
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs">{userInitials}</AvatarFallback>
                            </Avatar>
                        </div>
                    </div>
                </div>
            </motion.header>

            <main className="flex-1 container max-w-7xl mx-auto px-6 py-12 flex flex-col items-center">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-center w-full max-w-3xl space-y-4 mb-16"
                >
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-2">
                        Welcome back, {firstName}!
                    </h1>
                    <p className="text-lg text-muted-foreground">
                        Your next chapter awaits. What story will you tell today?
                    </p>

                    {/* Search Bar */}
                    <div className="relative mt-8 max-w-xl mx-auto">
                        <div className="relative group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                            <Input
                                placeholder="Search projects by title, character, or keyword..."
                                className="pl-11 pr-4 h-12 rounded-full bg-secondary/30 border-border focus-visible:bg-secondary/50 focus-visible:ring-1 focus-visible:ring-primary/50 text-base shadow-lg shadow-black/5"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        {/* <div className="flex items-center justify-center gap-6 mt-4 text-xs font-medium text-muted-foreground">
                            <button className="text-foreground hover:text-primary transition-colors">All Projects</button>
                            <button className="hover:text-foreground transition-colors">Sort by: Last Edited</button>
                            <button className="hover:text-foreground transition-colors">Filter by: Status</button>
                        </div> */}
                    </div>
                </motion.div>

                {/* Projects Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="w-full space-y-6"
                >
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-foreground/90">Recent Projects</h2>
                        <div className="flex items-center gap-2">
                            <ProjectListComponent.NewProjectButton />
                        </div>
                    </div>

                    <ProjectListComponent searchQuery={searchQuery} />
                </motion.div>
            </main>

            {/* Footer */}
            <motion.footer
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="w-full py-8 text-center text-xs text-muted-foreground border-t border-border mt-auto"
            >
                <div className="container px-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p>&copy; 2025 Plot Studio. All rights reserved.</p>
                    <div className="flex gap-6">
                        <a href="#" className="hover:text-foreground transition-colors">Documentation</a>
                        <a href="#" className="hover:text-foreground transition-colors">Support</a>
                        <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                            <span>System Operational</span>
                        </div>
                    </div>
                </div>
            </motion.footer>
        </div>
    );
}